import Button from '@dustin-web/microsite-components/src/button'
import { useSiteContext, useTranslations, useWebToggles } from '@dustin-web/microsite-components/src/hooks'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { StandardRegisterProps } from '.'
import { useGraphql, useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  GetCompanyInfo,
  GetCompanyInfoQuery,
  GetCompanyInfoQueryVariables,
} from 'src/graphql/generated/graphql-types'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import classNames from 'classnames'
import ValidationMessage from 'src/components/validationMessage'
import { GaRegEvent } from 'src/components/gaRegistrationHook/gaRegistrationEventHook'
import Icon from '@dustin-web/microsite-components/src/icon'
import { ModalDialog, ModalDialogContent, ModalDialogFooter, ModalDialogHeader } from '@dustin-web/microsite-components/src/modal-dialog'
import { State as GeneralState } from 'src/components/registerBusiness/standardRegister'
import {
  SendAccountRequestForExistingCompanyEmail,
  SendAccountRequestForExistingCompanyEmailMutation,
  SendAccountRequestForExistingCompanyEmailMutationVariables
} from 'src/graphql/generated/graphql-types'
import useShoppingCart from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'

enum State {
  editOrg,
  editCompanyName,
  displayCompanyName,
  runningQuery,
  error,
  modalError,
}

enum ShowOrganizationNumberErrorMessage {
  none,
  invalidOrganizationNumber,
  disallowNewContacts,
}

type Props = {
  onNavigateBack: () => void
  onStateChange: (state: GeneralState) => void
}

const CompanyInfo = ({ onNavigateBack, onStateChange }: Props) => {
  const { site } = useSiteContext()
  const { isToggleEnabled } = useWebToggles()
  const [isToggled] = useState<boolean>(isToggleEnabled('enable-new-user-request-form'))

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useFormContext<StandardRegisterProps>()
  const { translate } = useTranslations()

  const [mutation] = useGraphqlMutation<
    SendAccountRequestForExistingCompanyEmailMutation,
    SendAccountRequestForExistingCompanyEmailMutationVariables
  >(SendAccountRequestForExistingCompanyEmail)

  const [showOrganizationNumberErrorMessage, setShowOrganizationNumberErrorMessage] =
    useState<ShowOrganizationNumberErrorMessage>(ShowOrganizationNumberErrorMessage.none)
  const [showAdditonalUserRequestModal, setShowAdditonalUserRequestModal] = useState<boolean>(false)

  const watchIsAuthenticated: boolean = watch('isAuthenticated')
  const watchCompanyName: string = watch('companyName')
  const watchSetByOrgNumber: boolean = watch('companyNameSetByOrgNumber')
  const watchOrgNumber: string = watch('organizationNumber')
  const watchDisallowNewContacts: boolean = watch('disallowNewContacts')

  const { companyGaEvent, companyExistsGaEvent, companyyRequestOpenGaEvent, companyRequestSendGaEvent, companyCheckoutGaEvent, companyRequestConfirmGaEvent } = GaRegEvent()

  const { totalQuantity } = useShoppingCart({
    withRequiredEndUserInformation: true,
  })

  useEffect(() => {
    if (watchDisallowNewContacts) {
      setShowOrganizationNumberErrorMessage(ShowOrganizationNumberErrorMessage.disallowNewContacts)
      if (isToggled) {
        companyExistsGaEvent()
      }
    }
  }, [watchDisallowNewContacts])

  const [state, setState] = useState<State>(
    !watchCompanyName
      ? State.editOrg
      : watchSetByOrgNumber
        ? State.displayCompanyName
        : State.editCompanyName
  )

  const validOrgNumber: boolean = useMemo(() => {
    if (/^(?=.*[0-9])[0-9 -.]+$/.test(watchOrgNumber)) {
      const digits: string = watchOrgNumber.replace(/\D/g, '')

      switch (site) {
        case 'DustinSweden':
          return digits.length === 10
        case 'DustinNorway':
          return digits.length === 9 && ['8', '9'].includes(digits[0])
        case 'DustinDenmark':
        case 'DustinFinland':
          return digits.length === 8
        case 'DustinNetherlands': {
          const regex = new RegExp('^[0-9]{8}$')
          return regex.test(watchOrgNumber)
        }
        case 'DustinBelgium': {
          // Ten straight digits 0123456789 or in sections 0123.456.789   
          const regex = new RegExp('^(?:\\d{10}|\\d{4}\\.\\d{3}\\.\\d{3})$')
          return regex.test(watchOrgNumber)
        }
      }
    }

    return false
  }, [watchOrgNumber, site])

  useGraphql<GetCompanyInfoQuery, GetCompanyInfoQueryVariables>(GetCompanyInfo, {
    context: {
      skip: state !== State.runningQuery,
    },
    variables: {
      orgNumber: watchOrgNumber,
    },
    swrOptions: {
      revalidateIfStale: true,
      onSuccess: data => {
        const companyName: string | null = data?.companyInfo?.companyName ?? null
        const disallowNewContacts: boolean = data?.companyInfo?.disallowNewContacts ?? true
        const isRegisteredCompany: boolean = data?.companyInfo?.isRegisteredCompany ?? true
        const isInvalidOrganizationNumber: boolean =
          data?.companyInfo?.isInvalidOrganizationNumber ?? true
        const unkonwError: boolean = !data

        if (unkonwError) {
          setState(State.error)
          return
        }

        if (isInvalidOrganizationNumber || !isRegisteredCompany) {
          setShowOrganizationNumberErrorMessage(
            ShowOrganizationNumberErrorMessage.invalidOrganizationNumber
          )
          setState(State.editOrg)
          return
        }

        if (companyName) {
          setValue('companyName', companyName)
          setValue('companyNameSetByOrgNumber', true)
          setState(State.displayCompanyName)
        } else {
          setState(State.editCompanyName)
          setValue('companyNameSetByOrgNumber', false)
        }

        if (disallowNewContacts) {
          setValue('disallowNewContacts', true)
          setShowOrganizationNumberErrorMessage(
            ShowOrganizationNumberErrorMessage.disallowNewContacts
          )
          setState(State.editOrg)
          return
        }

        setShowOrganizationNumberErrorMessage(ShowOrganizationNumberErrorMessage.none)
        setValue('disallowNewContacts', disallowNewContacts)
      },
    },
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (
      showOrganizationNumberErrorMessage ===
      ShowOrganizationNumberErrorMessage.invalidOrganizationNumber
    ) {
      setShowOrganizationNumberErrorMessage(ShowOrganizationNumberErrorMessage.none)
    }

    setValue('organizationNumber', e.target.value)
  }

  const handleClickGet = () => {
    setState(State.runningQuery)
  }

  const handleClickUnlock = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (state === State.displayCompanyName) {
      setValue('companyName', '')
    }

    setState(State.editOrg)
  }

  return (
    <div>
      {state === State.runningQuery && <Spinner dustinSpinner />}
      <div className="row">
        <div className="col-12 col-lg-6 u-pr-lg-64 u-pb-lg-0 u-pb-24">
          <div className="row">
            <div className="col">
              <label htmlFor="register-org-number">
                {translate('RegisterBusiness_Form_OrgNumber')}
              </label>
            </div>
          </div>
          <div className="row">
            <div
              className={classNames('col-8 c-form__item c-form__item--mandatory', {
                'c-form__error':
                  showOrganizationNumberErrorMessage ===
                  ShowOrganizationNumberErrorMessage.invalidOrganizationNumber,
              })}>
              <input
                disabled={[State.editCompanyName, State.displayCompanyName].includes(state)}
                className={classNames({
                  'form-item-disabled': [State.editCompanyName, State.displayCompanyName].includes(
                    state
                  ),
                })}
                id="register-org-number"
                autoComplete="off"
                type="text"
                {...register('organizationNumber')}
                onChange={handleChange}
                aria-invalid={errors.organizationNumber ? 'true' : 'false'}
                aria-describedby={errors.organizationNumber ? 'organisationNumberError' : undefined}
              />
              {(showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.invalidOrganizationNumber ||
                errors.organizationNumber?.message) && (
                  <ValidationMessage id="organisationNumberError">
                    {translate('Validation_IncorrectOrgNumber')}
                  </ValidationMessage>
                )}
              {(showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.disallowNewContacts && !isToggled) && (
                  <ValidationMessage id="organisationNumberError">
                    {translate('Register_Error_DisallowNewContact')}
                  </ValidationMessage>
                )}
            </div>
            <div className="col-4 u-pl-4">
              {state === State.editOrg ? (
                <Button
                  id="btn-org-lookup"
                  type="button"
                  icon='download'
                  className="u-w-100"
                  style={{ minWidth: 'initial' }}
                  disabled={!validOrgNumber}
                  onClick={handleClickGet}
                  text={translate('RegisterBusiness_Form_OrgNumberLookup')}
                />
              ) : (
                <Button
                  type="button"
                  className="u-w-100"
                  onClick={handleClickUnlock}
                  text={translate('RegisterBusiness_Form_OrgNumberUnlock')}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col">
              <label htmlFor="register-company-name">{translate('Register_CompanyName')}</label>
            </div>
          </div>
          <div className="row">
            <div
              className={classNames('col c-form__item c-form__item--mandatory', {
                'c-form__error': errors.companyName,
              })}>
              <input
                disabled={state !== State.editCompanyName}
                className={classNames({ 'form-item-disabled': state !== State.editCompanyName })}
                id="register-company-name"
                autoComplete="off"
                type="text"
                placeholder={translate('CompanyName_Placeholder')}
                {...register('companyName')}
                aria-invalid={errors.companyName ? 'true' : 'false'}
                aria-describedby={errors.companyName ? 'companyNameError' : undefined}
              />
              {errors?.companyName && (
                <ValidationMessage id="companyNameError">
                  {translate('Validation_CompanyNameIsRequired')}
                </ValidationMessage>
              )}
              {(showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.disallowNewContacts && isToggled) && (
                  <div id="organisationNumberError" className='d-flex u-pt-8'>
                    <Icon name="info" className="u-mr-8 u-pt-2 i-20" />
                    <div>{translate('Register_Error_OrganizationAlreadyRegistered')}</div>
                  </div>
                )}
            </div>
          </div>
          <div className="row u-pt-12 justify-content-end">
            <div
              className={classNames('col-12 col-lg-auto', { 'u-pl-lg-4': !watchIsAuthenticated })}>
              {(showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.disallowNewContacts && isToggled) && (
                  <Button
                    id="btn-company-info-fill-request"
                    className="u-w-100 u-w-lg-auto"
                    primary
                    text={translate('RegisterBusiness_Request')}
                    onClick={() => {
                      setValue('additionalUserRequest', true)
                      setShowAdditonalUserRequestModal(true)
                      companyyRequestOpenGaEvent()
                    }}
                  />
                )}
              {(showOrganizationNumberErrorMessage !==
                ShowOrganizationNumberErrorMessage.disallowNewContacts) && (
                  <Button
                    id="btn-company-info-proceed"
                    disabled={(state !== State.displayCompanyName && state !== State.editCompanyName) || watchDisallowNewContacts}
                    className="u-w-100 u-w-lg-auto"
                    icon="arrow-right"
                    primary
                    type="submit"
                    text={translate('RegisterBuinsess_Procced')}
                    onClick={() => companyGaEvent()}
                  />
                )}
            </div>
            {!watchIsAuthenticated && (
              <div className="col-12 col-lg-auto u-pr-lg-4 order-lg-first">
                <Button
                  className="u-w-100 u-w-lg-auto"
                  text={translate('Registration_Back')}
                  onClick={onNavigateBack}
                />
              </div>
            )}
          </div>
          {state === State.error && (
            <div className="row">
              <div className="col-12">
                <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
              </div>
            </div>
          )}
        </div>
        {(totalQuantity > 0 && isToggled && showOrganizationNumberErrorMessage ===
          ShowOrganizationNumberErrorMessage.disallowNewContacts) && (
            <div className="col-12 col-lg-6 order-lg-last u-pl-lg-64 u-pt-lg-0 u-pt-24 u-bl-lg u-bt-lg-none u-bt">
              <h3 className='u-fs-14 u-fw-bold'>{translate("Register_Order_Without_Account_Header")}</h3>
              <p className='u-fs-14 u-pb-24'>{translate("Register_Order_Without_Account_Text")}</p>
              <Button
                id=""
                className="u-w-100 u-w-lg-auto c-btn--checkout "
                primary
                text={translate("Register_Order_Without_Account_Button")}
                onClick={() => {
                  companyCheckoutGaEvent()
                  window.location.href = '/checkoutsite/noaccount'
                }}
              />
            </div>
          )}
      </div>
      <ModalDialog
        show={showAdditonalUserRequestModal}
        isCloseable={true}
        handleClose={() => {
          setValue('additionalUserRequest', false)
          setShowAdditonalUserRequestModal(false)
        }}
        size="lg"
      >
        <ModalDialogHeader title={translate('RegisterBusiness_Additonal_User_Form_Title')} />
        <ModalDialogContent>
          <div className='u-mb-40 u-fs-16' >{translate('RegisterBusiness_Additonal_User_Form_Description')}</div>
          <div className="row u-mb-16" >
            {/* Org Number */}
            <div className="col-12 col-lg-6 u-mb-lg-0 u-mb-16">
              <label className='u-disabled' htmlFor="register-org-number">
                {translate('RegisterBusiness_Form_OrgNumber')}
              </label>
              <input
                disabled={true}
                className={classNames('u-bg-light-gray', { 'form-item-disabled': true })}
                id="register-org-number"
                type="text"
                {...register('organizationNumber')}
                onChange={handleChange}
                aria-invalid={'false'}
                aria-describedby={undefined}
              />
            </div>
            {/* Company Name */}
            <div className="col-12 col-lg-6">
              <label className='u-disabled' htmlFor="register-company-name">{translate('Register_CompanyName')}</label>
              <input
                disabled={true}
                className={classNames('u-bg-light-gray', { 'form-item-disabled': true })}
                id="register-company-name"
                type="text"
                placeholder={translate('CompanyName_Placeholder')}
                {...register('companyName')}
                aria-invalid={'false'}
              />
            </div>
          </div>

          <div className="row u-mb-16">
            {/* Email */}
            <div className="col-12 col-lg-6 u-mb-lg-0 u-mb-16">
              <label htmlFor="register-email">
                {translate('Register_email')}
              </label>
              <input
                className='no-mouseflow-dom u-bg-light-gray'
                id="email"
                type="text"
                {...register('email')}
                onChange={e => {
                  setValue('email', e.target.value)
                }}
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'emailError' : undefined}
              />
              {errors?.email && (
                <ValidationMessage id="emailError">
                  {translate('Validation_EmailIsRequired')}
                </ValidationMessage>
              )}
            </div>
            {/* Name */}
            <div className="col-12 col-lg-6">
              <label htmlFor="register-name">{translate('SelectContact_ContactList_Name')}</label>
              <input
                className='no-mouseflow-dom u-bg-light-gray'
                id="register-name"
                type="text"
                placeholder={translate('SelectContact_ContactList_Name')}
                {...register('name')}
                onChange={e => { setValue('name', e.target.value) }}
              />
              {errors.name?.type === 'max' && (
                <ValidationMessage id="firstNameError">
                  {translate('MaxLength', [translate('Register_Name'), '50'])}
                </ValidationMessage>
              )}
              {errors?.name?.type === 'required' && (
                <ValidationMessage id="nameError">
                  {translate('Validation_NameIsRequired')}
                </ValidationMessage>
              )}
            </div>
          </div>

          {/* Removed for now, likely to be added in the next itaration */}
          {/* Additional Information */}
          {/* <div className="row">
            <div className="no-mouseflow-dom col">
              <label htmlFor="register-additional-info">
                {translate('RegisterBusiness_Additonal_User_Form_Additional_Info')}
              </label>
              <textarea
                style={{ resize: 'none' }}
                className='u-bg-light-gray form-textarea'
                id="register-additional-info"
                {...register('additionalInformation')}
                onChange={e => { setValue('additionalInformation', e.target.value) }}
              />
            </div>
          </div> */}

          {state === State.modalError && (
            <div className="row">
              <div className="col-12">
                <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
              </div>
            </div>
          )}

          <ModalDialogFooter
            showCancel
            cancelText={translate('RegisterBusiness_Additonal_User_Form_Cancel')}
            onCancel={() => {
              setValue('additionalUserRequest', false)
              setShowAdditonalUserRequestModal(false)
            }}
            showConfirm
            className='modal-form-footer u-pr-0 u-pt-24'
            confirmText={translate('RegisterBusiness_Additonal_User_Form_SendRequest')}
            onConfirm={handleSubmit(async ({
              email,
              organizationNumber,
              companyName,
              name,
              // additionalInformation,
            }: StandardRegisterProps) => {
              setState(State.runningQuery)
              companyRequestSendGaEvent()
              const result: SendAccountRequestForExistingCompanyEmailMutation = await mutation({
                variables: {
                  companyName,
                  email,
                  orgNumber: organizationNumber,
                  name,
                  //additionalInformation
                },
              })

              if (result?.sendAccountRequestEmailForExistingCompany?.success) {
                companyRequestConfirmGaEvent()
                onStateChange(GeneralState.additionalUserRequestSent)
              } else {
                setState(State.modalError)
              }
            })}
          />
        </ModalDialogContent>
      </ModalDialog>
    </div>
  )
}

export default CompanyInfo
